import { useMutation, useQueryClient } from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { Team } from '@/types/core/team';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { teamMutationKeys, teamQueryKeys } from './query-keys';

/**
 * Hook to update a team.
 * Returns a react-query mutation that accepts partial team updates.
 * Waits until the read model has a newer _rev (or resource exists when backend doesn't send _rev yet).
 */
export function useUpdateTeam(tenantId?: UUID, teamId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: teamMutationKeys.updateTeam(tenantId, teamId),
    mutationFn: async (updates: Partial<Omit<Team, 'teamId'>>) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!teamId) throw new Error('Missing team id');

      const cachedTeam = qc.getQueryData<Team | null>(
        teamQueryKeys.detail(tenantId, teamId)
      );
      const minRev = cachedTeam?._rev ?? -2;

      await teamService.updateTeam(tenantId, teamId, {
        name: updates.name ?? '',
        description: updates.description,
      });

      const updatedTeam = await pollUntilResourceNewerThan(
        () => teamService.findTeam(tenantId, teamId),
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );

      return updatedTeam;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: teamQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: teamQueryKeys.detail(tenantId, teamId),
      });
    },
  });
}
